<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useDataTransfer } from 'dashboard/composables/useDataTransfer';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ManageDealLabelsDialog from 'dashboard/components-next/Deals/ManageDealLabelsDialog.vue';

/* eslint-enable vue/custom-event-name-casing */

const props = defineProps({
  showSelect: { type: Boolean, default: true },
});

/* eslint-disable vue/custom-event-name-casing */
const emit = defineEmits([
  'managePipeline',
  'import',
  'export',
  'select',
  'manage-fields',
]);
const { t } = useI18n();
const { checkPermissions } = usePolicy();
const { isUnified } = useDataTransfer();
const showActionsDropdown = ref(false);
const manageLabelsDialogRef = ref(null);

const transferMenuItems = legacyItems => {
  if (!checkPermissions(['administrator', 'contact_manage'])) return [];
  if (!isUnified.value) return legacyItems;
  return [
    {
      label: t('DATA_TRANSFER.MENU_IMPORT'),
      action: 'import',
      value: 'import',
      icon: 'i-lucide-download',
    },
    {
      label: t('DATA_TRANSFER.MENU_EXPORT'),
      action: 'export',
      value: 'export',
      icon: 'i-lucide-upload',
    },
  ];
};

const menuItems = computed(() => [
  ...(props.showSelect
    ? [
        {
          label: t('DEALS_BULK_ACTIONS.SELECT_DEALS'),
          action: 'select',
          value: 'select',
          icon: 'i-lucide-check-square',
        },
      ]
    : []),
  ...(checkPermissions(['administrator'])
    ? [
        {
          label: t('DEALS.ACTIONS.MANAGE_PIPELINE'),
          action: 'managePipeline',
          value: 'managePipeline',
          icon: 'i-lucide-settings-2',
        },
      ]
    : []),
  ...(checkPermissions(['administrator'])
    ? [
        {
          label: t('DEALS.LABELS.MANAGE_LABELS'),
          action: 'manage-labels',
          value: 'manage-labels',
          icon: 'i-lucide-tags',
        },
      ]
    : []),
  {
    label: t('ATTRIBUTES_MGMT.MANAGE_FIELDS'),
    action: 'manage-fields',
    value: 'manage-fields',
    icon: 'i-lucide-settings-2',
  },
  ...transferMenuItems([
    {
      label: t('DEALS.EXPORT.MENU_LABEL'),
      action: 'export',
      value: 'export',
      icon: 'i-lucide-upload',
    },
    {
      label: t('DEALS.IMPORT.MENU_LABEL'),
      action: 'import',
      value: 'import',
      icon: 'i-lucide-download',
    },
  ]),
]);

const handleAction = ({ action }) => {
  showActionsDropdown.value = false;
  if (action === 'managePipeline') emit('managePipeline');
  else if (action === 'import') emit('import');
  else if (action === 'export') emit('export');
  else if (action === 'select') emit('select');
  // eslint-disable-next-line vue/custom-event-name-casing
  else if (action === 'manage-fields') emit('manage-fields');
  else if (action === 'manage-labels')
    manageLabelsDialogRef.value?.dialogRef?.open();
};
</script>

<template>
  <div v-on-clickaway="() => (showActionsDropdown = false)" class="relative">
    <Button
      icon="i-lucide-ellipsis-vertical"
      color="slate"
      variant="ghost"
      size="sm"
      :class="showActionsDropdown ? 'bg-n-alpha-2' : ''"
      @click="showActionsDropdown = !showActionsDropdown"
    />
    <DropdownMenu
      v-if="showActionsDropdown"
      :menu-items="menuItems"
      class="ltr:right-0 rtl:left-0 mt-1 w-52 top-full"
      @action="handleAction($event)"
    />
    <ManageDealLabelsDialog ref="manageLabelsDialogRef" />
  </div>
</template>
